import { useEffect } from 'react';
import { parseISO, formatKoreanFull } from '@/lib/dates';
import type { CalendarEvent } from '@/types';
import styles from './EventDetail.module.css';

const COLOR_MAP: Record<string, string> = {
  blue: 'var(--event-blue)',
  purple: 'var(--event-purple)',
  green: 'var(--event-green)',
  orange: 'var(--event-orange)',
  pink: 'var(--event-pink)',
  cyan: 'var(--event-cyan)',
};

interface Props {
  event: CalendarEvent;
  onEdit: (event: CalendarEvent) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}

export function EventDetail({ event, onEdit, onDelete, onClose }: Props) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const accentColor = COLOR_MAP[event.color] || COLOR_MAP.blue;
  const dateLabel = (() => {
    try {
      return formatKoreanFull(parseISO(event.date));
    } catch {
      return event.date;
    }
  })();

  const timeLabel = event.isAllDay
    ? '종일'
    : event.startTime
      ? `${event.startTime}${event.endTime ? ` – ${event.endTime}` : ''}`
      : null;

  function handleDelete() {
    if (confirm(`"${event.title}" 일정을 삭제할까요?`)) {
      onDelete(event.id);
    }
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Color bar */}
        <div className={styles.colorBar} style={{ background: accentColor }} />

        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>{event.title}</h2>
          <button className={styles.closeBtn} onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M4.5 4.5L13.5 13.5M13.5 4.5L4.5 13.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Details */}
        <div className={styles.details}>
          <div className={styles.detailRow}>
            <svg className={styles.icon} width="14" height="14" viewBox="0 0 14 14" fill="none">
              <rect x="1" y="2" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1" />
              <path d="M4.5 1v2M9.5 1v2M1 5.5h12" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
            </svg>
            <span>{dateLabel}</span>
          </div>

          {timeLabel && (
            <div className={styles.detailRow}>
              <svg className={styles.icon} width="14" height="14" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1" />
                <path d="M7 4v3l2 1.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>{timeLabel}</span>
            </div>
          )}

          {event.description && (
            <div className={styles.description}>
              <p>{event.description}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <button className={styles.deleteBtn} onClick={handleDelete}>
            삭제
          </button>
          <button className={styles.editBtn} onClick={() => onEdit(event)}>
            수정
          </button>
        </div>
      </div>
    </div>
  );
}
